import { Component, inject, computed, signal, HostListener } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PublicI18nService } from '@frontend/public-calendar';
import { HeaderStateService, NavLink } from '@frontend/dedicated-calendar-preview';

@Component({
  selector: 'dc-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule],
  template: `
    <div class="min-h-screen">
      <router-outlet />
      
      <!-- Floating Language Toggle Button (hidden when header exists) -->
      @if (!headerState.hasHeaderSection()) {
        <button
          type="button"
          (click)="toggleLanguage()"
          class="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-card/90 backdrop-blur-sm border border-border rounded-full shadow-lg hover:bg-card hover:shadow-xl transition-all duration-200"
          [title]="currentLocale() === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'"
        >
          <svg class="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
          <span class="text-sm font-medium text-foreground">{{ currentLocale() === 'de' ? 'EN' : 'DE' }}</span>
        </button>
      }
      
      <!-- Mobile Menu Drawer (slide from right) -->
      @if (headerState.isMobileMenuOpen()) {
        <!-- Backdrop -->
        <div 
          class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity"
          (click)="closeMobileMenu()"
          (keyup.escape)="closeMobileMenu()"
        ></div>
        
        <!-- Drawer Panel -->
        <div class="fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-card shadow-xl transform transition-transform duration-300 ease-in-out animate-slide-in-right">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-border">
            @if (headerState.logoUrl()) {
              <img [src]="headerState.logoUrl()!" [alt]="headerState.logoText()" class="h-8 w-auto" />
            } @else {
              <span class="text-lg font-semibold text-foreground">{{ headerState.logoText() || 'Menu' }}</span>
            }
            <button
              type="button"
              class="-m-2.5 rounded-md p-2.5 text-foreground hover:text-foreground"
              (click)="closeMobileMenu()"
            >
              <span class="sr-only">Close menu</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Navigation Links -->
          <div class="px-6 py-6 space-y-2">
            @for (link of headerState.navLinks(); track $index) {
              <a
                [href]="getNavLinkHref(link)"
                class="block rounded-lg px-3 py-2 text-base font-semibold text-foreground hover:bg-background"
                (click)="onMobileNavClick($event, link)"
              >
                {{ getNavLinkLabel(link) }}
              </a>
            }
          </div>
          
          <!-- Divider -->
          <div class="border-t border-border mx-6"></div>
          
          <!-- Language Toggle -->
          <div class="px-6 py-6">
            <p class="text-sm font-medium text-muted-foreground mb-3">{{ currentLocale() === 'de' ? 'Sprache' : 'Language' }}</p>
            <div class="flex gap-2">
              <button
                type="button"
                (click)="setLanguage('de')"
                [class]="currentLocale() === 'de' 
                  ? 'flex-1 px-4 py-2 rounded-lg bg-brand-600 text-white font-medium' 
                  : 'flex-1 px-4 py-2 rounded-lg bg-muted text-foreground font-medium hover:bg-muted'"
              >Deutsch</button>
              <button
                type="button"
                (click)="setLanguage('en')"
                [class]="currentLocale() === 'en' 
                  ? 'flex-1 px-4 py-2 rounded-lg bg-brand-600 text-white font-medium' 
                  : 'flex-1 px-4 py-2 rounded-lg bg-muted text-foreground font-medium hover:bg-muted'"
              >English</button>
            </div>
          </div>
        </div>
      }
      
      <!-- Scroll to Top Button -->
      @if (showScrollToTop()) {
        <button
          type="button"
          (click)="scrollToTop()"
          class="fixed bottom-6 right-6 z-40 p-3 bg-card/90 backdrop-blur-sm border border-border rounded-full shadow-lg hover:bg-card hover:shadow-xl transition-all duration-200"
          [class.animate-pulse-subtle]="!isAtTop()"
          title="Scroll to top"
        >
          <svg class="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      }
    </div>
  `,
  styles: [`
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }
    
    .animate-slide-in-right {
      animation: slideInRight 0.3s ease-out;
    }
    
    @keyframes pulseSubtle {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.85;
        transform: scale(1.05);
      }
    }
    
    .animate-pulse-subtle {
      animation: pulseSubtle 2s ease-in-out infinite;
    }
  `],
})
export class AppComponent {
  private readonly i18n = inject(PublicI18nService);
  protected readonly headerState = inject(HeaderStateService);
  
  readonly currentLocale = computed(() => this.i18n.locale());
  
  // Scroll position tracking
  private readonly _scrollY = signal(0);
  readonly isAtTop = computed(() => this._scrollY() < 100);
  readonly showScrollToTop = computed(() => this._scrollY() > 300);
  
  @HostListener('window:scroll')
  onScroll(): void {
    this._scrollY.set(window.scrollY);
  }
  
  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.headerState.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }
  }
  
  toggleLanguage(): void {
    const newLocale = this.currentLocale() === 'de' ? 'en' : 'de';
    this.i18n.setLocale(newLocale);
  }
  
  setLanguage(locale: 'de' | 'en'): void {
    this.i18n.setLocale(locale);
  }
  
  closeMobileMenu(): void {
    this.headerState.closeMobileMenu();
  }
  
  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  /** Get the URL/href for a navigation link */
  getNavLinkHref(link: NavLink): string {
    if (link.linkType === 'scroll' && link.targetSectionAnchor) {
      return `#section-${link.targetSectionAnchor}`;
    }
    if (link.linkType === 'legal' || link.type === 'legal') {
      if (link.legalKey === 'imprint') return '/legal/imprint';
      if (link.legalKey === 'privacy') return '/legal/privacy';
    }
    return link.url || '#';
  }
  
  /** Get the display label for a navigation link */
  getNavLinkLabel(link: NavLink): string {
    const locale = this.i18n.locale();
    if (locale === 'en' && link.labelEn) return link.labelEn;
    if (locale === 'de' && link.labelDe) return link.labelDe;
    return link.labelDe || link.labelEn || link.label || '';
  }
  
  /** Handle mobile nav link click - close menu and scroll if needed */
  onMobileNavClick(event: MouseEvent, link: NavLink): void {
    // Close menu first
    this.closeMobileMenu();
    
    // If it's a scroll link, handle the scroll
    if (link.linkType === 'scroll' && link.targetSectionAnchor) {
      event.preventDefault();
      
      // Small delay to let menu close animation complete
      setTimeout(() => {
        const targetId = `section-${link.targetSectionAnchor}`;
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }
}
